import { Client as BelfastClient } from '@kaizenplatform/belfast';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { PricingMerchandiseOption, PricingPoint, PricingTable, PricingTableCell } from '@kaizenplatform/hugot-entity';
import { PricingPointKey } from '@kaizenplatform/hugot-entity/dist/entity/PricingPoint/PricingPoint';
import { AxiosResponse } from 'axios';
import { History } from 'history';
import Store from 'repatch';

import HttpClient from '../adapter/HttpClient';
import State from '../core/State';
import { CheckDiscountCodeResult } from '../entity/DiscountCode';
import { SearchAssetResult } from '../entity/SearchAsset';
import UploadingFile from '../entity/UploadingFile';
import { Currency, DynamicSchemaDefinitionField, Organization } from '../generated/graphql';
import { adaptForTheDefinition, createWithDefinition } from '../graphql/dynamic_schema';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import ProjectWebApi from '../service/persistent/ProjectWebApi';
import SearchAssetApi from '../service/persistent/SearchAssetApi';
import { decrement, increment } from '../thunk/fullScreenSpinner';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';
import {
  addSelectedPricingMerchandiseOptionsId,
  removeSelectedPricingMerchandiseOptionsId,
  setAvailablePricingMerchandiseOptions,
} from '../thunk/pricingMerchandiseOptions';
import { clearDiscountCode, setAssetFormTermAgreements, setDiscountCode } from '../thunk/projectEditing';
import generateTempId from '../utility/generateTempId';
import { AnyFieldValue, FilesValue as CODEGEN_FilesValue } from '../view/types/DynamicSchema';
import { ExtendedProject as CODEGEN_ExtendedProject } from '../view/types/ExtendedProject';
import { FileReference as CODEGEN_FileReference } from '../view/types/Files';

class EditProjectUsecase {
  private readonly store: Store<State>;
  private readonly history: History;
  private readonly belfastClient: BelfastClient;
  private readonly errorPresenter: ErrorPresenter;
  private readonly messageDispatcher: MessageDispatcher;
  private readonly projectWebApi: ProjectWebApi;
  private readonly searchAssetApi: SearchAssetApi;

  constructor({
    store,
    history,
    belfastClient,
    errorPresenter,
    messageDispatcher,
    projectWebApi,
    searchAssetApi,
  }: {
    store: Store<State>;
    history: History;
    belfastClient: BelfastClient;
    errorPresenter: ErrorPresenter;
    messageDispatcher: MessageDispatcher;
    projectWebApi: ProjectWebApi;
    searchAssetApi: SearchAssetApi;
  }) {
    this.store = store;
    this.history = history;
    this.belfastClient = belfastClient;
    this.errorPresenter = errorPresenter;
    this.messageDispatcher = messageDispatcher;
    this.projectWebApi = projectWebApi;
    this.searchAssetApi = searchAssetApi;
  }

  // Used for Codegen functions that don't have access to `store` state
  public setFullScreenLoader = (fullScreenLoader: boolean) => {
    if (fullScreenLoader) {
      this.store.dispatch(increment());
    } else {
      this.store.dispatch(decrement());
    }
  };

  // GET API
  public setAssetFormTermAgreements = async (): Promise<void> => {
    const user = this.store.getState().authentication.signedInUser;
    if (user != null) {
      const isAgreed = await this.searchAssetApi.getUserTermAgreements(user.id);
      this.store.dispatch(setAssetFormTermAgreements(isAgreed));
    }
  };

  // POST
  public postAssetFormTermAgreements = async (): Promise<void> => {
    const user = this.store.getState().authentication.signedInUser;
    if (user != null) {
      try {
        await this.searchAssetApi.postUserTermAgreements();
        this.setAssetFormTermAgreements();
      } catch (error) {
        this.errorPresenter.showAsNonFatal(new Error('message.export_performance_report_request_has_failed'));
        error.log(error);
      }
    }
  };

  public setDiscountCode = (discountCode: string, discountCodeResult: CheckDiscountCodeResult): void => {
    this.store.dispatch(setDiscountCode(discountCode, discountCodeResult));
  };

  public clearDiscountCode = (): void => {
    this.store.dispatch(clearDiscountCode());
  };

  public uploadAssetFiles = async ({
    files,
    bucketId,
    assetCategoryFieldKey,
    addUploadingAssetFiles,
    updateUploadingAssetFilesProgress,
    handleFileUploadFinished,
  }: {
    files: ReadonlyArray<UploadingFile>;
    bucketId: string;
    assetCategoryFieldKey: string;
    addUploadingAssetFiles: (files: ReadonlyArray<UploadingFile>, assetCategoryFieldKey: string) => void;
    updateUploadingAssetFilesProgress: (file: UploadingFile, assetCategoryFieldKey: string, progress: number) => void;
    handleFileUploadFinished: (
      file: UploadingFile,
      fileReference: CODEGEN_FileReference,
      assetCategoryFieldKey: string,
    ) => void;
  }) => {
    const uploadingFiles = this._validateUploadingFiles([...files]);

    const { lastAccessedOrganization } = this.store.getState();
    if (lastAccessedOrganization == null) {
      return;
    }

    if (uploadingFiles) {
      addUploadingAssetFiles(uploadingFiles, assetCategoryFieldKey);

      for (const file of uploadingFiles) {
        const fileReference = await this.belfastClient.uploadFile(file, bucketId, lastAccessedOrganization.id, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
            updateUploadingAssetFilesProgress(file, assetCategoryFieldKey, progress);
          },
        });
        handleFileUploadFinished(file, fileReference, assetCategoryFieldKey);
      }
    }
  };

  public submitSearchAssetFile = async ({
    selectedSearchAsset,
    bucketId,
    fieldKey,
    addUploadingAssetFiles,
    updateUploadingAssetFilesProgress,
    handleFileUploadFinished,
    updateAssetCategoryValueInDraft,
  }: {
    selectedSearchAsset: SearchAssetResult[number] | null;
    bucketId: BucketId;
    fieldKey: string;
    addUploadingAssetFiles: (files: ReadonlyArray<UploadingFile>, assetCategoryFieldKey: string) => void;
    updateUploadingAssetFilesProgress: (
      file: UploadingFile | { id: string; name: string },
      assetCategoryFieldKey: string,
      progress: number,
      progressState?: 'uploading' | 'downloading',
    ) => void;
    handleFileUploadFinished: (
      file: UploadingFile,
      fileReference: CODEGEN_FileReference,
      assetCategoryFieldKey: string,
    ) => void;
    updateAssetCategoryValueInDraft: () => void;
  }) => {
    if (selectedSearchAsset == null) {
      return;
    }

    const state = this.store.getState();
    if (state.lastAccessedOrganization == null) {
      return;
    }
    const organizationId = state.lastAccessedOrganization.id;

    const fileId = generateTempId();

    const extractFileNameResult = /^https:\/\/(.+\/)*(.+__kz__)?([^?]+)(.*fm=([a-zA-Z]+))?/.exec(
      selectedSearchAsset.originalUrl,
    );

    const fileName =
      extractFileNameResult != null
        ? `${decodeURI(extractFileNameResult[3])}${
            extractFileNameResult[5] != null ? `.${extractFileNameResult[5]}` : ''
          }`
        : `search_file_${fileId}`;

    const dummyFile = { id: fileId, name: fileName };

    const client = new HttpClient({ baseUrl: '' });
    const response = await client.get(selectedSearchAsset.originalUrl, {
      withCredentials: false,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
        updateUploadingAssetFilesProgress(dummyFile, fieldKey, progress, 'downloading');
      },
    });

    const file = this.responseToFile(response, fileId, fileName);

    addUploadingAssetFiles([file], fieldKey);
    const fileReference = await this.belfastClient.uploadFile(file, bucketId, organizationId, {
      onUploadProgress: (progressEvent) => {
        const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
        updateUploadingAssetFilesProgress(file, fieldKey, progress);
      },
    });

    handleFileUploadFinished(file, fileReference, fieldKey);

    updateAssetCategoryValueInDraft();
  };

  private responseToFile = (response: AxiosResponse, fileId: string, fileName: string): UploadingFile => {
    const blob = new Blob([response.data]);
    const file = new File([blob], fileName, { type: response.data.type }) as UploadingFile;
    file.id = fileId;

    return file;
  };

  // TODO: Belfast ClientをUsecaseが保持しているので、現状は例えロジックがUsecase/Entityと関係なくともUsecaseから呼んでいる。
  public uploadBriefFiles = async ({
    files,
    values,
    bucketId,
    addUploadingBriefFiles,
    updateUploadingFilesProgress,
    handleFileUploadFinished,
  }: {
    files: ReadonlyArray<UploadingFile>;
    values: Record<string, AnyFieldValue>;
    bucketId: string;
    addUploadingBriefFiles: (files: ReadonlyArray<UploadingFile>, creativeBriefFieldKey: string) => void;
    updateUploadingFilesProgress: (fileId: string, progress: number) => void;
    handleFileUploadFinished: (
      file: UploadingFile,
      fileReference: FileReference,
      creativeBriefFieldKey: string,
    ) => void;
  }) => {
    const creativeBriefFieldKey = Object.keys(values)[Object.values(values).indexOf(bucketId)];
    const uploadingFiles = this._validateUploadingFiles([...files]);

    const { lastAccessedOrganization } = this.store.getState();
    if (lastAccessedOrganization == null) {
      return;
    }

    if (uploadingFiles) {
      addUploadingBriefFiles(uploadingFiles, creativeBriefFieldKey);

      for (const file of uploadingFiles) {
        const fileReference = await this.belfastClient.uploadFile(file, bucketId, lastAccessedOrganization.id, {
          onUploadProgress: (progressEvent) => {
            const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
            updateUploadingFilesProgress(file.id, progress);
          },
        });

        handleFileUploadFinished(file, fileReference, creativeBriefFieldKey);
      }
    }
  };

  private _validateUploadingFiles = (files: UploadingFile[]) => {
    let uploadingFiles;

    try {
      uploadingFiles = files.map((file) => {
        // Can not upload files larger than 5GB. (5GB -> 5368709120 Byte)
        if (file.size > 5368709120) {
          throw new Error('message.uploading_files_is_larger_than_5gb');
        }

        // Generate a random character string in `file.id` for `Progress Bar`.
        const uploadingId = generateTempId();
        file.id = uploadingId;
        return file;
      });
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    }
    return uploadingFiles;
  };

  // v2素材のファイル削除 - 下書き時(filesから物理削除)
  public deleteAssetFile = async ({
    fileReference,
    fieldKey,
    bucketId,
    removeAssetFile,
  }: {
    fileReference: CODEGEN_FileReference;
    fieldKey: string;
    bucketId?: string | null;
    removeAssetFile: (fileReference: CODEGEN_FileReference, assetCategoryFieldKey: string) => void;
  }) => {
    if (bucketId == null) return;
    removeAssetFile(fileReference, fieldKey);
    await this.belfastClient.deleteFile(fileReference.id, bucketId);
  };

  public deleteBriefFile = async ({
    fileReference,
    values,
    bucketId,
    removeBriefFile,
  }: {
    fileReference: CODEGEN_FileReference;
    values: Record<string, AnyFieldValue>;
    bucketId: BucketId;
    removeBriefFile: (fileReference: CODEGEN_FileReference, creativeBriefFieldKey: string) => void;
  }) => {
    // FIXME: `creativeBriefFieldKey`の簡単な取得方法はないか？
    const creativeBriefFieldKey = Object.keys(values)[Object.values(values).indexOf(bucketId)];
    removeBriefFile(fileReference, creativeBriefFieldKey);

    await this.belfastClient.deleteFile(fileReference.id, bucketId);
  };

  public createDraftProjectFromExistingOneAndStartEdit = async ({
    organizationId,
    parentProjectId,
  }: {
    organizationId: string;
    parentProjectId: string;
  }): Promise<void> => {
    this.store.dispatch(increment());

    const language = this.store.getState().language;

    const parentProject = await this.projectWebApi.getByIdForEdit(parentProjectId, language);

    const creativeBriefValue = adaptForTheDefinition(
      parentProject.creativeBriefValue.values,
      parentProject.projectType.creativeBriefDefinition,
    );

    // assetCategoryValueは初期化する
    if (parentProject.projectType.assetCategoryDefinition == null) {
      throw new Error('parentProject.projectType.assetCategoryDefinition must not null.');
    }
    const assetCategoryValue = createWithDefinition(parentProject.projectType.assetCategoryDefinition);

    const projectId = await this.projectWebApi.createAsDraft({
      title: parentProject.title,
      matchingFormat: 'auto',
      projectType: parentProject.projectType,
      creativeBriefValue,
      assetCategoryValue,
      organizationId,
      parentProjectId,
    });

    this.history.replace(`/projects/${projectId}/edit`);

    this.store.dispatch(decrement());
  };

  public notifyUnimportantMessage = (message: string) => {
    this.messageDispatcher.notifyUnimportant(message);
  };

  public updateSelectedProductOption = (id: string, checked: boolean) => {
    if (checked) {
      this.store.dispatch(addSelectedPricingMerchandiseOptionsId(id));
    } else {
      this.store.dispatch(removeSelectedPricingMerchandiseOptionsId(id));
    }
  };

  public updateWindowDataLayer = (submittedProject: CODEGEN_ExtendedProject, pointTotal?: number) => {
    const { authentication, acct, lastAccessedOrganization } = this.store.getState();

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'project-published',
        user_id: authentication.signedInUser ? authentication.signedInUser.id : null,
        user_email: authentication.signedInUser ? authentication.signedInUser.email : null,
        user_name: authentication.signedInUser ? authentication.signedInUser.name : null,
        organization_id: submittedProject.organization.id,
        organization_name: submittedProject.organization.name,
        project_id: submittedProject.id,
        project_title: submittedProject.title,
        region: lastAccessedOrganization ? lastAccessedOrganization.businessRegion.key : null,
        point_total: pointTotal ? (acct.productPricing ? pointTotal * acct.productPricing.flatRate : pointTotal) : null,
      });
    }
  };

  public getBriefFiles = async (project: CODEGEN_ExtendedProject) => {
    const briefFilesBuilder = {} as Record<string, ReadonlyArray<CODEGEN_FileReference>>;

    const allFileFields = project.projectType.creativeBriefDefinition.sections
      .reduce<Array<DynamicSchemaDefinitionField>>((fields, section) => [...fields, ...section.fields], [])
      .filter((field) => field.type === 'FILES');

    for (let i = 0; i < allFileFields.length; i++) {
      const field = allFileFields[i];
      const entityFileReferences = await this.belfastClient.getAllFilesInBucket(
        (project.creativeBriefValue.values[field.key] as CODEGEN_FilesValue)!,
      );
      const normalizedFileReferences = entityFileReferences.map((ref) => ref.toJSON()) as Array<CODEGEN_FileReference>;
      briefFilesBuilder[field.key] = normalizedFileReferences;
    }

    return briefFilesBuilder;
  };

  public getAssetFiles = async (bucketId: string | null) => {
    if (bucketId == null) return [];

    const entityFiles = await this.belfastClient.getAllFilesInBucket(bucketId);
    return (entityFiles.map((file) => file.toJSON()) as unknown) as ReadonlyArray<CODEGEN_FileReference>;
  };

  public setLastAccessedOrganization = (organization: Organization) => {
    this.store.dispatch(setLastAccessedOrganization(organization));
  };

  public setAvailablePricingMerchandiseOptions = (project: CODEGEN_ExtendedProject) => {
    // Apollo Clientが取得するObjectを、Entityのクラスに変換するのに、深層まで型変換、クラス定義を行わないといけない。
    this.store.dispatch(
      setAvailablePricingMerchandiseOptions(
        project.projectType.pricingMerchandiseOptions.map(
          (option) =>
            new PricingMerchandiseOption({
              ...option,
              helpUrlForDirector: option.helpUrlForDirector || undefined,
              helpUrlForOptimizer: option.helpUrlForOptimizer || undefined,
              pricingTables: option.pricingTables.map(
                (table) =>
                  new PricingTable({
                    ...table,
                    cells: table.cells.map(
                      (cell) =>
                        new PricingTableCell({
                          ...cell,
                          prices: cell.prices.map(
                            (price) =>
                              new PricingPoint({
                                ...price,
                                key: price.key as PricingPointKey,
                                rewardCurrency: price.rewardCurrency as Currency,
                              }),
                          ),
                          supportingPlaybackTimeLength: cell.supportingPlaybackTimeLength ?? null,
                        }),
                    ),
                  }),
              ),
            }),
        ) || [],
      ),
    );
  };

  public showFatalErrorMessage = (error: Error) => {
    this.errorPresenter.showAsFatal(error);
  };

  public showNonFatalErrorMessage = (error: Error) => {
    this.errorPresenter.showAsNonFatal(error);
  };
}

export default EditProjectUsecase;
